import { ArrowRight, Pause, Square, AlertTriangle, SatelliteDish, WifiOff } from 'lucide-react';
import { STATUS_CONFIG } from '@/lib/utils';
import useStatusDisplayStyle from '@/hooks/useStatusDisplayStyle';
import Tooltip from './Tooltip';

// Resolve a STATUS_CONFIG.icon slug to a Lucide component. Keep this in sync
// with the icon: field in packages/design-tokens/src/status.ts — every status
// must have a corresponding entry here or the component falls back to a dot.
const ICONS = {
  'arrow-right': ArrowRight,
  'pause': Pause,
  'square': Square,
  'alert-triangle': AlertTriangle,
  'satellite-dish': SatelliteDish,
  'wifi-off': WifiOff,
};

/**
 * Single render site for vehicle-status indicators. Resolves the user's
 * display-style preference at render time and emits one of three shapes:
 *
 *  - 'icon':         colored glyph only
 *  - 'dotWithIcon':  colored circle with a small white glyph inside
 *  - 'dotAndIcon':   colored dot + glyph side-by-side (GPSgate-style)
 *
 * Props:
 *  - status:    a VehicleStatus key ('moving' | 'idle' | 'stopped' | 'offline' | 'pending')
 *  - size:      icon edge length in px (default 14)
 *  - style:     optional override for the user preference (used by the preview tiles)
 *  - className: applied to the outer wrapper
 */
export default function StatusIndicator({ status, size = 14, style: forcedStyle, className = '', title }) {
  const { style: prefStyle } = useStatusDisplayStyle();
  const style = forcedStyle ?? prefStyle;
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return null;
  const Icon = ICONS[cfg.icon];
  const color = cfg.color;

  // Square glyph is filled per the GPSgate reference; other icons stay stroked.
  const isFilledShape = cfg.icon === 'square';
  const iconFill = isFilledShape ? color : 'none';

  let inner;
  if (style === 'dotWithIcon') {
    // Colored circle with the white glyph stamped inside.
    const dotSize = size + 8;
    inner = (
      <span
        className={`inline-flex items-center justify-center rounded-full ${className}`}
        style={{ width: dotSize, height: dotSize, backgroundColor: color }}
        aria-label={status}
      >
        {Icon ? <Icon size={Math.max(8, size - 2)} color="#ffffff" strokeWidth={3} /> : null}
      </span>
    );
  } else if (style === 'dotAndIcon') {
    inner = (
      <span className={`inline-flex items-center gap-1.5 ${className}`} aria-label={status}>
        <span
          className="inline-block rounded-full"
          style={{
            width: Math.max(6, size - 6),
            height: Math.max(6, size - 6),
            backgroundColor: color,
            boxShadow: `0 0 4px ${color}50`,
          }}
        />
        {Icon ? <Icon size={size} color={color} fill={iconFill} strokeWidth={2.5} /> : null}
      </span>
    );
  } else {
    // 'icon' (default) — colored glyph only.
    inner = (
      <span className={`inline-flex items-center justify-center ${className}`} aria-label={status}>
        {Icon ? <Icon size={size} color={color} fill={iconFill} strokeWidth={2.5} /> : (
          <span
            className="inline-block rounded-full"
            style={{ width: size, height: size, backgroundColor: color }}
          />
        )}
      </span>
    );
  }

  return <Tooltip content={title}>{inner}</Tooltip>;
}
